import { Box, Chip, lighten, Typography } from "@mui/material";
import { TFunction } from "i18next";
import { RefObject, SyntheticEvent } from "react";
import DropdownMenu from "../../../../components/DropdownMenu";
import { StatusChip } from "../../../../components/form/StatusChip";
import { SessionAction, SessionStatus } from "../../../../data/models/session";
import { StudyOverviewDTO } from "../../../../data/models/studyOverview";
import i18n from "../../../../data/translations/i18n";
import { router } from "../../../../router/Routes";
import { compareStringDates, getDateAndTimeFormatByPlatformLanguage, getDateFormatByPlatformLanguage } from "../../../../utils/utils";
import { TableHeader } from "../../../../_styles/table/TableHeader";
import { TableText } from "../../../../_styles/table/TableText";
import { getDueDateStatusProps } from "../../../Screening/Process/Session/dueDateStatusProps";
import { getEventStatusProps } from "../../../Screening/Process/Session/eventStatusProps";
import { getSessionStatusChip } from "../../../Screening/Process/Session/statusProps";

export const studyOverviewColumns = (t: TFunction,
    handleStatusChange: (sessionId: string, action: SessionAction) => Promise<void>,
    onNoShow: (eventId: string, isAssessment: boolean, title: string) => Promise<void>,
    revokeClientFromStudyExclusion: (sessionId: string) => Promise<void>,
    navigate: (path: string) => void,
    popperAnchorRef: RefObject<HTMLButtonElement>,
    handleStatusSelect: (e: SyntheticEvent<HTMLElement>, clientId: string) => void,
    timelineStatuses: Record<string, boolean>,
    checkAndStoreTimelineStatus: (eventId: string) => void) => {
    const options = (
        sessionId: string,
        sessionStatus: SessionStatus, group: string,
        currentEventData: {
            id: string,
            isAssessment: boolean,
            title: string,
            timeline: string
        },
        upcomingEventData: {
            id: string,
            isAssessment: boolean,
            title: string
        }) => {
        const baseOptions = [
            {
                id: 'emergency',
                name: `${t("SESSION_EMERGENCY")}`,
                onClick: async () => {
                    await handleStatusChange(sessionId, SessionAction.EMERGENCY);
                },
                customStyle: {
                    color: lighten("#dbd600", 0.1),
                    shadow: '0px 2px 2px rgba(0, 0, 0, 0.25)',
                },
                disabled: sessionId === null
            },
            {
                id: 'resume',
                name: `${t("CLIENT_RESUME")}`,
                onClick: async () => {
                    await handleStatusChange(sessionId, SessionAction.RESUME);
                },
                customStyle: {
                    color: lighten('#2a9d8f', .3),
                    shadow: '0px 2px 2px rgba(0, 0, 0, 0.25)',
                },
                disabled: false
            },
            {
                id: 'deactivate',
                name: `${t("SESSION_DEACTIVATE")}`,
                onClick: async () => {
                    await handleStatusChange(sessionId, SessionAction.DEACTIVATE);
                },
                customStyle: {
                    color: lighten('#FF0000', .3),
                    shadow: '0px 2px 2px rgba(0, 0, 0, 0.25)',
                },
                disabled: sessionId === null
            },
            {
                id: 'no-show-up-current',
                name: `${t("CURRENT_EVENT_NO_SHOW")}`,
                onClick: async () => {
                    await onNoShow(currentEventData?.id, currentEventData?.isAssessment, currentEventData?.title);
                },
                customStyle: {
                    color: 'gray',
                },
                disabled: !currentEventData || !currentEventData?.id || currentEventData.isAssessment
            },
            {
                id: 'no-show-up-upcoming',
                name: `${t("UPCOMING_EVENT_NO_SHOW")}`,
                onClick: async () => {
                    await onNoShow(upcomingEventData?.id, upcomingEventData?.isAssessment, upcomingEventData?.title);
                },
                customStyle: {
                    color: 'gray',
                },
                disabled: !upcomingEventData || !upcomingEventData?.id || timelineStatuses[upcomingEventData.id]
            },
            {
                id: 'revoke',
                name: `${t("REVOKE")}`,
                onClick: async () => {
                    await revokeClientFromStudyExclusion(sessionId);
                },
                customStyle: {
                    color: 'gray',
                },
                disabled: currentEventData.timeline !== "Baseline"
                    || !sessionId
                    || [SessionStatus.EMERGENCY_HOLD, SessionStatus.ABORT].includes(sessionStatus)
            }
        ];

        return sessionStatus === SessionStatus.EMERGENCY_HOLD
            ? baseOptions.filter(option => option.id !== 'emergency')
            : baseOptions.filter(option => option.id !== 'resume')

    };

    function navigateToResouceTool(eventId: string): void {
        if (!eventId) return;
        router.navigate(`/resource-planning/view/${sessionStorage.getItem('viewType')}`, { state: { eventId: eventId } })
    }

    return [
        {
            header: (
                <TableHeader fontSize={"13px !important"}>{t("CLIENT")}</TableHeader>
            ),
            key: "clientName",
            render: (row: StudyOverviewDTO) => (
                <TableText
                    title={row.clientName}
                    onClick={() => navigate(`/users/${row.clientId}/about`)}
                    style={{ cursor: 'pointer', color: 'inherit', textDecoration: "underline" }}
                >
                    {row.clientName}
                </TableText>
            ),
            className: "process-cell",
            sort: (a: StudyOverviewDTO, b: StudyOverviewDTO) => {
                return a.clientName
                    .toLowerCase()
                    .localeCompare(b.clientName.toLowerCase());
            },
        },
        {
            header: (
                <TableHeader fontSize={"13px !important"}>{t("CLIENT_RESPONSIBLE_PEOPLE")}</TableHeader>
            ),
            key: "responsiblePeople",
            render: (row: StudyOverviewDTO) => (
                <TableText>{row.responsiblePeople.join(", ")}</TableText>
            ),
            className: "process-cell",
            sort: (a: StudyOverviewDTO, b: StudyOverviewDTO) => {
                return a.responsiblePeople[0]
                    ?.toLowerCase()
                    .localeCompare(b.responsiblePeople[0]?.toLowerCase());
            },
        },
        {
            header: (
                <TableHeader fontSize={"13px !important"}>{t("SESSION_TIMELINE")}</TableHeader>
            ),
            key: "timeline",
            render: (row: StudyOverviewDTO) => (
                <TableText>{row.timeline}</TableText>
            ),
            className: "process-cell",
            sort: (a: StudyOverviewDTO, b: StudyOverviewDTO) => {
                return a.timeline
                    ?.toLowerCase()
                    .localeCompare(b.timeline?.toLowerCase());
            },
        },
        {
            header: (
                <TableHeader fontSize={"13px !important"}>{t("CURRENT_EVENT")}</TableHeader>
            ),
            key: "currentEvent",
            render: (row: StudyOverviewDTO) => (
                row.currentEvent && (
                    <TableText
                        onClick={() => navigateToResouceTool(row.currentEventId)}
                    >
                        <StatusChip
                            sx={{
                                cursor: row.currentEventId && "pointer",
                            }}
                            titleSx={{
                                textDecoration: row.currentEventId && "underline",
                            }}
                            title={row.currentEvent}
                            status={row.eventStatus}
                            attendeeStatus=""
                            t={t}
                            dueDate={row.dueDate}
                            getStatusProps={getEventStatusProps}
                        />
                    </TableText>
                )
            ),
            className: "process-cell",
            // sort: (a: StudyOverviewDTO, b: StudyOverviewDTO) => {
            //     return a.currentEvent
            //         ?.toLowerCase()
            //         .localeCompare(b.currentEvent?.toLowerCase());
            // },
        },
        {
            header: <TableHeader>{t("CLIENT_PROCESS_STATUS")}</TableHeader>,
            key: "sessionStatus",
            render: (row: StudyOverviewDTO) => (
                <div>
                    <Box
                        ref={popperAnchorRef}
                        onClick={(e) => handleStatusSelect(e, row.clientId)}
                    >
                        {getSessionStatusChip(row.sessionStatus, t)}
                    </Box>
                </div>
            ),
            sort: (a: StudyOverviewDTO, b: StudyOverviewDTO) => {
                return a.sessionStatus
                    ?.toLowerCase()
                    .localeCompare(b.sessionStatus?.toLowerCase());
            },
        },

        {
            header: (
                <TableHeader fontSize={"13px !important"}>{t("EVENT_START_DATE")}</TableHeader>
            ),
            key: "date",
            render: (row: StudyOverviewDTO) => (
                row.date && (<TableText>{getDateAndTimeFormatByPlatformLanguage(new Date(row.date), i18n.language)}</TableText>
                )
            ),
            className: "process-cell",
            sort: (a: StudyOverviewDTO, b: StudyOverviewDTO) => {
                return compareStringDates(a.date, b.date, i18n);
            },
        },
        {
            header: (
                <TableHeader fontSize={"13px !important"}>{t("UPCOMING_EVENT")}</TableHeader>
            ),
            key: "upcomingEvent",
            render: (row: StudyOverviewDTO) => (
                <TableText>
                    {row.upcomingEvent}
                    {' '}
                    {row.upcomingEvent?.includes("PM1") && row.isATSPostponed &&
                        <Chip size='small' label='ATS' sx={{ bgcolor: lighten('#e63946', 0.8), height: "26px", fontSize: '11px', border: `1px solid #e63946` }} />
                    }
                </TableText>
            ),
            className: "process-cell",
            sort: (a: StudyOverviewDTO, b: StudyOverviewDTO) => {
                return a.upcomingEvent?.toLowerCase().localeCompare(b.upcomingEvent?.toLowerCase());
            },
        },
        {
            header: (
                <TableHeader fontSize={"13px !important"}>{t("TODO_DATE")}</TableHeader>
            ),
            key: "dueDate",
            render: (row: StudyOverviewDTO) => (
                row.dueDate && (
                    <Typography
                        onClick={() => navigateToResouceTool(row.upcomingEventId)}
                    >
                        <StatusChip
                            sx={{
                                cursor: row.upcomingEventId && "pointer",
                            }}
                            titleSx={{
                                textDecoration: row.upcomingEventId && "underline",
                            }}
                            title={
                                row.upcomingEventStatus === "PROJECTED"
                                    ? getDateFormatByPlatformLanguage(new Date(row.dueDate), i18n.language)
                                    : getDateAndTimeFormatByPlatformLanguage(new Date(row.dueDate), i18n.language)
                            }
                            status={row.upcomingEventStatus}
                            attendeeStatus={row.upcomingEventAttendeeStatus}
                            t={t}
                            dueDate={row.dueDate}
                            getStatusProps={getDueDateStatusProps}
                        />
                    </Typography>
                )
            ),
            className: "process-cell",
            sort: (a: StudyOverviewDTO, b: StudyOverviewDTO) => {
                return compareStringDates(a.dueDate, b.dueDate, i18n);
            },
        },
        {
            header: (
                <TableHeader fontSize={"13px !important"}>{t("GENERAL_GROUP")}</TableHeader>
            ),
            key: "group",
            render: (row: StudyOverviewDTO) => (
                <TableText>{row.group}</TableText>
            ),
            className: "process-cell",
            sort: (a: StudyOverviewDTO, b: StudyOverviewDTO) => {
                return a.group?.toLowerCase().localeCompare(b.group?.toLowerCase());
            },
        },
        {
            header: (
                <TableHeader fontSize={"13px !important"} width={5}></TableHeader>
            ),
            key: "action",
            render: (row: StudyOverviewDTO) => (
                <TableText onClick={() => checkAndStoreTimelineStatus(row.upcomingEventId)}>
                    {
                        <DropdownMenu options={
                            options(
                                row.sessionId,
                                row.sessionStatus,
                                row.group,
                                {
                                    id: row.currentEventId,
                                    isAssessment: !(
                                        row.timeline.toLowerCase().includes('pm') ||
                                        row.timeline.toLowerCase().includes('booster')
                                    ),
                                    title: row.currentEvent, timeline: row.timeline
                                },
                                {
                                    id: row.upcomingEventId,
                                    isAssessment: !(
                                        row.upcomingEvent?.toLowerCase().includes('pm') ||
                                        row.upcomingEvent?.toLowerCase().includes('booster')
                                    ),
                                    title: row.upcomingEvent
                                })} />}</TableText>
            ),
            width: 3,
            className: "process-cell",
        },
    ];
}

